import React, { useMemo } from 'react';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { useGetReleaseOverviewQuery } from '../../../../../redux/api/fymAPI';
import { ReleaseTab, ReleaseTabProps } from '../releaseTabProps';
import { daysUntilRelease } from '../releaseOrder';
import { planMode } from '../plan/planMode';
import { useOpenReleaseCalendar } from '../plan/useOpenReleaseCalendar';
import { buildRevisionCards, newSince } from '../assets/revisionCards';
import { useKitPoll } from '../assets/useKitPoll';
import { useKitZip } from '../assets/useKitZip';
import { SetupPanel } from './SetupPanel';
import { DonePanel } from './DonePanel';
import { PlannedPanel } from './PlannedPanel';
import { ContentMadePanel } from './ContentMadePanel';
import { CanStillMakePanel } from './CanStillMakePanel';
import { FanReleasePanel } from './FanReleasePanel';
import { ServicesPanel } from '../../../servicesPanel/ServicesPanel';
import { useIsDemoAccount } from '../../../../accountSelector/useIsDemoAccount';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface OverviewTabProps extends ReleaseTabProps {
  onOpenTab: (tab: ReleaseTab) => void;
  /** The Theme panel of the song (closed by default, design b1). */
  themePanel: React.ReactNode;
}

/**
 * The Overview tab (Releases v2, design b1–b4): the marketing summary of one song. Left: Setup (no
 * form, "Fix in Catalog" per item), Done, Planned and Content made. Right: "Send to your
 * subscribers" (design presave-subscriptions d2), Can still make and the Theme. Each panel loads on
 * its own (design e1, e2): the asset panels read the kit answer, which the page loaded; the setup,
 * the posts and the fan release read `GET /releases/:uuid`.
 */
const OverviewTabComponent: React.FC<OverviewTabProps> = ({ accountId, item, release, onOpenTab, themePanel }) => {
  const overview = useGetReleaseOverviewQuery({ accountId, catalogItemUuid: item.uuid });
  const isDemo = useIsDemoAccount();
  const data = useKitPoll({ accountId, catalogItemUuid: item.uuid, release });
  const zip = useKitZip({ accountId, catalogItemUuid: item.uuid });
  const openCalendar = useOpenReleaseCalendar(item.uuid);
  const now = useMemo(() => new Date(), []);
  const mode = useMemo(() => planMode(item.releaseDate, now), [item.releaseDate, now]);
  const days = useMemo(() => daysUntilRelease(item.releaseDate, now), [item.releaseDate, now]);
  const cards = useMemo(
    () => buildRevisionCards({ assets: data.assets, newest: data.newest, reels: data.reels }),
    [data.assets, data.newest, data.reels]
  );
  const since = useMemo(() => newSince(overview.data?.lastViewedAt ?? null, now), [now, overview.data]);
  const { refetch } = overview;
  const retry = (): void => {
    refetch().catch(console.error);
  };

  let posts: React.ReactNode;
  if (overview.isLoading) {
    posts = <LoadingView />;
  } else if (overview.data === undefined) {
    posts = <ErrorView error={overview.error} retry={retry} />;
  } else {
    posts = (
      <>
        <SetupPanel catalogItemUuid={item.uuid} setup={overview.data.setup} />
        <DonePanel
          overview={overview.data}
          isUpcoming={days !== null && days >= 0}
          onOpenCalendar={() => openCalendar()}
        />
        <PlannedPanel
          accountId={accountId}
          item={item}
          mode={mode}
          posts={overview.data.posts}
          onOpenTab={onOpenTab}
          onOpenCalendar={() => openCalendar()}
        />
      </>
    );
  }

  return (
    <div className={styles.grid} data-testid="release-overview">
      <div className={styles.column}>
        {/* design: soundcloud-connector d1 — "Where it plays". Moved from the Kit tab (owner, 2026-10-19). */}
        <ServicesPanel accountId={accountId} item={item} readOnly={isDemo} />
        {posts}
        <ContentMadePanel cards={cards} since={since} zip={zip} onOpenTab={onOpenTab} />
      </div>
      <div className={styles.column}>
        <FanReleasePanel
          accountId={accountId}
          catalogItemUuid={item.uuid}
          title={item.title}
          fanRelease={overview.data?.fanRelease ?? null}
          onCheckAgain={retry}
        />
        <CanStillMakePanel accountId={accountId} item={item} cards={cards} />
        {themePanel}
      </div>
    </div>
  );
};

export const OverviewTab = TypedMemo(OverviewTabComponent);
